import { Component, ElementRef, Inject, LOCALE_ID, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DecimalPipe } from '@angular/common';

import { AuthService } from 'src/app/shared';
import {
    CalculationType, LocationCalculationDetail, LocationDetailView,
    LocationOrigin, QualityCriterionType, RiskAssessmentStatus, ScreeningResultHistoryItem
} from 'src/app/contracts';
import { CalculationService, LocationService, ScreeningService } from 'src/app/services';

@Component({
    selector: 'app-location-details',
    templateUrl: './location-details.component.html',
    styleUrls: ['./location-details.component.css']
})
export class LocationDetailsComponent implements OnInit {
    @ViewChild('approveCalculationModalButton') private readonly approveCalculationModalButton: ElementRef;
    @ViewChild('addCommentModalButton') private readonly addCommentModalButton: ElementRef;
    @ViewChild('unapproveCalculationModalButton') private readonly unapproveCalculationModalButton: ElementRef;
    @ViewChild('deleteCalculationModalButton') private readonly deleteCalculationModalButton: ElementRef;
    @ViewChild('showScreeningHistoryButton') private readonly showScreeningHistoryButton: ElementRef;

    public location: LocationDetailView = new LocationDetailView({});
    public get publicActionPerformed(): boolean {
        return !!this.location && this.location.origin !== LocationOrigin.V1asV2Calculation;
    }

    public locationOrigin = LocationOrigin;
    public riskAssessmentStatus = RiskAssessmentStatus;
    public qualityCriterionType = QualityCriterionType;

    // Current calculation when doing modal-stuff
    public modalCalculationId: string;

    public isExternalUser = false;

    public screeningHistories: Array<ScreeningResultHistoryItem>;
    public isHistoryLoaded = false;

    public get allNoneApprovedCalculations(): any[] {
        return [
            ...(this.location?.unapprovedCalculations ?? []),
            ...(this.location?.v1Calculations ?? [])
        ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }

    constructor(
        @Inject(LOCALE_ID) private readonly locale,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly authService: AuthService,
        private readonly locationService: LocationService,
        private readonly calculationService: CalculationService,
        private readonly screeningService: ScreeningService) {
    }

    public ngOnInit(): void {
        this.isExternalUser = this.authService.isExternalUser();

        const id = this.route.snapshot.paramMap.get('id');
        this.locationService.getLocationDetail(id).subscribe(res => {
            res.unapprovedCalculations = res.unapprovedCalculations
                .sort((a, b) => new Date(a.createdAt) > new Date(b.createdAt) ? -1 : 1);
            res.approvedCalculations = res.approvedCalculations
                .sort((a, b) => new Date(a.createdAt) > new Date(b.createdAt) ? -1 : 1);

            res.screeningResults = res.screeningResults
                .sort((a, b) => a.excessFactor > b.excessFactor ? -1 : 1);

            this.location = res;

            if (this.isExternalUser) {
                this.location.origin = LocationOrigin.PublicAction;
            }
        });
    }

    public newCalculation(screeningCalculation: LocationCalculationDetail): void {
        if (screeningCalculation) {
            this.calculationService.createFromScreening(this.location.id,
                this.location.name,
                this.location.number,
                screeningCalculation.compoundCasNr,
                screeningCalculation.compoundName,
                screeningCalculation.screeningData,
                CalculationType.Standard);
        } else {
            this.calculationService.createTempEmpty(this.location.id,
                this.location.name,
                this.location.number,
                CalculationType.Standard);
        }
        this.router.navigate(['riskassessment/select-model']);
    }

    public goToCalculationResults(id: string): void {
        this.router.navigate(['riskassessment/calculation-result/' + id]);
    }

    public showAddCommentModal(calculationId: string): void {
        this.modalCalculationId = calculationId;
        this.addCommentModalButton.nativeElement.click();
    }

    public showApproveCalculationModal(calculationId: string): void {
        this.modalCalculationId = calculationId;
        this.approveCalculationModalButton.nativeElement.click();
    }

    public showUnapproveCalculationModal(calculationId: string): void {
        this.modalCalculationId = calculationId;
        this.unapproveCalculationModalButton.nativeElement.click();
    }

    public showDeleteCalculationModal(calculationId: string): void {
        this.modalCalculationId = calculationId;
        this.deleteCalculationModalButton.nativeElement.click();
    }

    public showScreeningHistoryModal(): void {
        this.showScreeningHistoryButton.nativeElement.click();

        const id = this.route.snapshot.paramMap.get('id');
        this.screeningService.getScreeningResultHistories({ locationId: id }).subscribe(res => {
            let index = 0;

            this.screeningHistories = res.items.reverse().map(p => {
                const item = new ScreeningResultHistoryItem(p);
                item.dataTarget = 'target' + (index++);
                return item;
            });

            this.isHistoryLoaded = true;
        });

        this.isHistoryLoaded = true;
    }

    private findCalculation(calcId: string): any {
        return [
            ...(this.location.unapprovedCalculations ?? []),
            ...(this.location.approvedCalculations ?? []),
            ...(this.location.v1Calculations ?? [])
        ].find(c => c.id === calcId);
    } 


    public addCommentFromModal(comment: string): void {
        const calcId = this.modalCalculationId;
        this.calculationService.addCommentToCalculation(calcId, comment).subscribe(res => {
            let calc = this.findCalculation(calcId);
            if (calc) {
                calc.latestComment = comment;
            }

            this.modalCalculationId = '';
        });
    }

    public approveCalculationFromModal(comment: string): void {
        const calcId = this.modalCalculationId;
        this.calculationService.approveCalculation(calcId, comment).subscribe(res => {
            let calc = this.findCalculation(calcId);
            if (calc) {
                calc.latestComment = comment;

                this.location.approvedCalculations = this.location.approvedCalculations.concat(calc)
                    .sort((a, b) => a.createdAt > b.createdAt ? -1 : 1);

                this.location.unapprovedCalculations = this.location.unapprovedCalculations
                    .filter(c => c.id !== calcId);

                this.location.v1Calculations = this.location.v1Calculations
                    .filter(c => c.id !== calcId);

                this.modalCalculationId = '';
            }
        });
    }

    public unapproveCalculationFromModal(comment: string): void {
        const calcId = this.modalCalculationId;
        this.calculationService.unapproveCalculation(calcId, comment).subscribe(res => {
            const calc = this.location.approvedCalculations.find(c => c.id === calcId);
            calc.caseWorker.name = res.caseWorkerName;
            calc.latestComment = comment;
            this.location.unapprovedCalculations = this.location.unapprovedCalculations.concat(calc)
                .sort((a, b) => a.createdAt > b.createdAt ? -1 : 1);
            this.location.approvedCalculations = this.location.approvedCalculations
                .filter(c => c.id !== calcId);
            this.modalCalculationId = '';
        });
    }

    public deleteCalculationFromModal(): void {
        const calcId = this.modalCalculationId;
        this.calculationService.deleteCalculation(calcId).subscribe(res => {
            
            this.location.unapprovedCalculations = this.location.unapprovedCalculations
                .filter(c => c.id !== calcId);

            this.location.v1Calculations = this.location.v1Calculations
                .filter(c => c.id !== calcId);

            this.modalCalculationId = '';
        });
    }

    public getValidNumber(val: number): number {
        const result = (!val || isNaN(val) || !Number.isFinite(val)) ? 0 : val;
        return result;
    }

    public getNumberWithSignificantDigits(val: number): string {
        const n = this.getValidNumber(val);
        let digitsInfo = '';
        if (n >= 10.0) { // xx.x
            digitsInfo = '1.1-1';
        }
        else if (n >= 1.0) { // x.xx
            digitsInfo = '1.2-2';
        }
        else if (n >= 0.1) { // 0.xxx
            digitsInfo = '1.3-3';
        }
        else if (n >= 0.01) { // 0.0xxx
            digitsInfo = '1.4-4';
        }
        else { // 0.00xxx
            digitsInfo = '1.5-5';
        }

        const result = new DecimalPipe(this.locale).transform(n, digitsInfo);
        return result;
    }

    public getEllipsisText(text: string, maxLength: number): string {
        if (text !== null) {
            return text.length > maxLength ? text.substr(0, maxLength) + '...' : text;
        }
        return '';
    }
}
